import type {
  CampaignDashboardCampaign,
  CampaignDashboardPayload,
  CampaignDonorListItem,
  CampaignSaleListItem,
  CampaignSalesChartPoint,
} from '@/types/campaign-report'

export type DashboardDatePreset = '7d' | '30d' | 'month' | 'all' | 'custom'

/** Períodos do seletor da página Início (`/home`), alinhados ao backend `date_from` / `date_to`. */
export type HomeDashboardPeriodId = '30d' | '7d' | '1a' | 'hoje' | 'all'

export function getHomeDashboardPeriodRange(
  period: HomeDashboardPeriodId,
  campaignStartsAt?: string | null,
  now: Date = new Date()
): { dateFrom: string; dateTo: string } {
  const current = new Date(now)
  current.setHours(0, 0, 0, 0)

  switch (period) {
    case '30d': {
      const r = getPresetRange('30d', current, campaignStartsAt)
      return { dateFrom: r.dateFrom, dateTo: r.dateTo }
    }
    case '7d': {
      const r = getPresetRange('7d', current, campaignStartsAt)
      return { dateFrom: r.dateFrom, dateTo: r.dateTo }
    }
    case '1a': {
      const end = formatDateInput(current)
      const start = new Date(current)
      start.setDate(start.getDate() - 364)
      return { dateFrom: formatDateInput(start), dateTo: end }
    }
    case 'hoje': {
      const d = formatDateInput(current)
      return { dateFrom: d, dateTo: d }
    }
    case 'all': {
      const r = getPresetRange('all', current, campaignStartsAt)
      return { dateFrom: r.dateFrom, dateTo: r.dateTo }
    }
    default: {
      const r = getPresetRange('30d', current, campaignStartsAt)
      return { dateFrom: r.dateFrom, dateTo: r.dateTo }
    }
  }
}

export interface DashboardDateRange {
  preset: DashboardDatePreset
  dateFrom: string
  dateTo: string
}

export interface DashboardComparison {
  current: number
  previous: number | null
  deltaPercent: number | null
  isPositive: boolean | null
  isCapped: boolean
}

/** Baseline minimo do periodo anterior em relacao ao valor atual para exibir %. */
const MIN_PREVIOUS_TO_CURRENT_RATIO = 0.01

/** Limite exibido nos KPIs; valores acima aparecem como >999%. */
const MAX_DELTA_PERCENT = 999

export interface DashboardInsights {
  revenuePerDonor: number
  revenuePerNumber: number
  averageRevenuePerDay: number
  averagePaymentsPerDay: number
  averageNumbersPerDay: number
  daysWithoutSales: number
  bestRevenueDay: CampaignSalesChartPoint | null
  worstRevenueDay: CampaignSalesChartPoint | null
  peakPaymentsDay: CampaignSalesChartPoint | null
  projectedSelloutDate: string | null
  soldNumbers: number
  remainingNumbers: number | null
  alerts: string[]
}

const DAY_IN_MS = 24 * 60 * 60 * 1000
const MAX_RANGE_DAYS = 366

/** Data civil `YYYY-MM-DD` (sem hora) — evita parse UTC de `new Date('YYYY-MM-DD')`. */
const CALENDAR_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

function toDate(value: string): Date {
  return new Date(`${value}T00:00:00`)
}

/**
 * Interpreta datas de calendário (`YYYY-MM-DD`) como meia-noite local.
 * ISO com hora continua sendo parseado pelo motor (UTC/offset do próprio valor).
 */
export function parseDisplayDate(value: string): Date {
  const trimmed = value.trim()
  if (CALENDAR_DATE_RE.test(trimmed)) {
    return toDate(trimmed)
  }
  return new Date(trimmed)
}

/** Dia do mês no eixo do gráfico (ex.: `04`), alinhado ao calendário local. */
export function formatChartDay(value: string): string {
  const date = parseDisplayDate(value)
  if (Number.isNaN(date.getTime())) return value
  return String(date.getDate()).padStart(2, '0')
}

function pad(value: number): string {
  return String(value).padStart(2, '0')
}

/** Data local em `YYYY-MM-DD` (uso em filtros de API). */
export function formatDateInput(value: Date): string {
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
}

/** Garante que o intervalo nao ultrapasse o limite do backend (366 dias). */
export function capDateRange(dateFrom: string, dateTo: string): { dateFrom: string; dateTo: string } {
  const from = toDate(dateFrom)
  const to = toDate(dateTo)
  
  const diffDays = Math.round((to.getTime() - from.getTime()) / DAY_IN_MS)
  
  if (diffDays >= MAX_RANGE_DAYS) {
    const newFrom = new Date(to.getTime())
    newFrom.setDate(to.getDate() - (MAX_RANGE_DAYS - 1))
    return {
      dateFrom: formatDateInput(newFrom),
      dateTo: dateTo
    }
  }
  
  return { dateFrom, dateTo }
}

export function getAllPeriodRange(
  campaignStartsAt?: string | null,
  now: Date = new Date()
): DashboardDateRange {
  const current = new Date(now)
  current.setHours(0, 0, 0, 0)

  const campaignStartDate =
    campaignStartsAt && !Number.isNaN(new Date(campaignStartsAt).getTime())
      ? formatDateInput(new Date(campaignStartsAt))
      : '2024-01-01'

  const range = capDateRange(campaignStartDate, formatDateInput(current))

  return {
    preset: 'all',
    dateFrom: range.dateFrom,
    dateTo: range.dateTo,
  }
}

export function getPresetRange(
  preset: Exclude<DashboardDatePreset, 'custom'>,
  now: Date = new Date(),
  campaignStartsAt?: string | null
): DashboardDateRange {
  const current = new Date(now)
  current.setHours(0, 0, 0, 0)

  if (preset === 'all') {
    return getAllPeriodRange(campaignStartsAt, current)
  }

  if (preset === 'month') {
    const start = new Date(current.getFullYear(), current.getMonth(), 1)
    const end = new Date(current.getFullYear(), current.getMonth() + 1, 0)
    return {
      preset,
      dateFrom: formatDateInput(start),
      dateTo: formatDateInput(end),
    }
  }

  const days = preset === '7d' ? 7 : 30
  const start = new Date(current)
  start.setDate(start.getDate() - (days - 1))

  return {
    preset,
    dateFrom: formatDateInput(start),
    dateTo: formatDateInput(current),
  }
}

export function buildPreviousRange(range: Pick<DashboardDateRange, 'dateFrom' | 'dateTo'>) {
  const from = toDate(range.dateFrom)
  const to = toDate(range.dateTo)
  const diffDays = Math.max(
    1,
    Math.round((to.getTime() - from.getTime()) / DAY_IN_MS) + 1
  )

  const previousTo = new Date(from.getTime() - DAY_IN_MS)
  const previousFrom = new Date(previousTo)
  previousFrom.setDate(previousTo.getDate() - (diffDays - 1))

  return {
    dateFrom: formatDateInput(previousFrom),
    dateTo: formatDateInput(previousTo),
  }
}

export function calculateDelta(current: number, previous: number | null): DashboardComparison {
  if (previous === null || Number.isNaN(previous) || previous <= 0) {
    return {
      current,
      previous,
      deltaPercent: null,
      isPositive: null,
      isCapped: false,
    }
  }

  if (current > 0 && previous / current < MIN_PREVIOUS_TO_CURRENT_RATIO) {
    return {
      current,
      previous,
      deltaPercent: null,
      isPositive: null,
      isCapped: false,
    }
  }

  const rawPercent = ((current - previous) / previous) * 100
  const isCapped = Math.abs(rawPercent) > MAX_DELTA_PERCENT
  const deltaPercent = Number(
    (isCapped
      ? rawPercent > 0
        ? MAX_DELTA_PERCENT
        : -MAX_DELTA_PERCENT
      : rawPercent
    ).toFixed(2)
  )

  return {
    current,
    previous,
    deltaPercent,
    isPositive: deltaPercent >= 0,
    isCapped,
  }
}

export function formatDeltaPercentLabel(
  comparison: DashboardComparison
): { text: string; up: boolean | null } {
  if (comparison.deltaPercent === null || comparison.isPositive === null) {
    return { text: '—', up: null }
  }

  if (comparison.isCapped) {
    const cappedText =
      comparison.deltaPercent > 0 ? `>999%` : `<-999%`
    return { text: cappedText, up: comparison.isPositive }
  }

  const sign = comparison.deltaPercent > 0 ? '+' : ''
  return { text: `${sign}${comparison.deltaPercent}%`, up: comparison.isPositive }
}

export function formatDeltaPercentMagnitude(comparison: DashboardComparison): string | null {
  if (comparison.deltaPercent === null || comparison.isPositive === null) {
    return null
  }

  const magnitude = Math.abs(comparison.deltaPercent)
  return comparison.isCapped ? `>${magnitude}%` : `${magnitude}%`
}

export function deriveDashboardInsights(
  payload: CampaignDashboardPayload
): DashboardInsights {
  const { campaign, summary, sales_chart: salesChart } = payload
  const points = salesChart.points
  const totalDays = Math.max(points.length, 1)
  const totalRevenue = summary.total_revenue
  const totalPayments = summary.total_payments
  const soldNumbers = summary.total_quantity

  const bestRevenueDay = points.reduce<CampaignSalesChartPoint | null>((best, point) => {
    if (!best || point.amount > best.amount) return point
    return best
  }, null)

  const worstRevenueDay = points.reduce<CampaignSalesChartPoint | null>((worst, point) => {
    if (!worst || point.amount < worst.amount) return point
    return worst
  }, null)

  const peakPaymentsDay = points.reduce<CampaignSalesChartPoint | null>((peak, point) => {
    if (!peak || point.payments_count > peak.payments_count) return point
    return peak
  }, null)

  const revenuePerDonor =
    summary.total_donors > 0 ? Number((totalRevenue / summary.total_donors).toFixed(2)) : 0
  const revenuePerNumber =
    soldNumbers > 0 ? Number((totalRevenue / soldNumbers).toFixed(2)) : 0
  const averageRevenuePerDay = Number((totalRevenue / totalDays).toFixed(2))
  const averagePaymentsPerDay = Number((totalPayments / totalDays).toFixed(2))
  const averageNumbersPerDay = Number((soldNumbers / totalDays).toFixed(2))
  const daysWithoutSales = points.filter((point) => point.payments_count === 0).length

  const remainingNumbers =
    campaign.total_numbers !== null && campaign.total_numbers !== undefined
      ? Math.max(campaign.total_numbers - soldNumbers, 0)
      : null

  let projectedSelloutDate: string | null = null
  if (
    remainingNumbers !== null &&
    remainingNumbers > 0 &&
    averageNumbersPerDay > 0 &&
    points.length > 0
  ) {
    const lastPointDate = toDate(points[points.length - 1].date)
    const daysToSellout = Math.ceil(remainingNumbers / averageNumbersPerDay)
    const projectedDate = new Date(lastPointDate)
    projectedDate.setDate(projectedDate.getDate() + daysToSellout)
    projectedSelloutDate = projectedDate.toISOString()
  }

  const alerts: string[] = []
  if (totalPayments === 0) alerts.push('Nenhuma venda registrada no periodo selecionado.')
  if (!campaign.total_numbers) alerts.push('Campanha sem total de numeros configurado.')
  if (!campaign.price || Number(campaign.price) <= 0) alerts.push('Campanha sem preco valido configurado.')
  if (campaign.status === 'finished') alerts.push('Campanha marcada como finalizada.')
  if (campaign.status === 'cancelled') alerts.push('Campanha marcada como cancelada.')

  return {
    revenuePerDonor,
    revenuePerNumber,
    averageRevenuePerDay,
    averagePaymentsPerDay,
    averageNumbersPerDay,
    daysWithoutSales,
    bestRevenueDay,
    worstRevenueDay,
    peakPaymentsDay,
    projectedSelloutDate,
    soldNumbers,
    remainingNumbers,
    alerts,
  }
}

export function isWithinRange(
  value: string | null | undefined,
  dateFrom: string,
  dateTo: string
): boolean {
  if (!value) return false
  const target = new Date(value).getTime()
  const from = toDate(dateFrom).getTime()
  const to = new Date(`${dateTo}T23:59:59`).getTime()
  return target >= from && target <= to
}

export function filterDonorsByRange(
  donors: CampaignDonorListItem[],
  dateFrom: string,
  dateTo: string
): CampaignDonorListItem[] {
  return donors.filter((donor) =>
    isWithinRange(donor.last_payment_paid_at, dateFrom, dateTo)
  )
}

export function filterSalesByRange(
  sales: CampaignSaleListItem[],
  dateFrom: string,
  dateTo: string
): CampaignSaleListItem[] {
  return sales.filter((sale) => isWithinRange(sale.paid_at, dateFrom, dateTo))
}

export type DonorSortKey =
  | 'name'
  | 'email'
  | 'phone'
  | 'last_payment_paid_at'
  | 'last_payment_amount'
  | 'total_amount_in_campaign'
  | 'payments_count_in_campaign'

export type SalesSortKey =
  | 'payment_id'
  | 'donor_name'
  | 'email'
  | 'amount'
  | 'quantity'
  | 'paid_at'
  | 'created_at'

function normalizeText(value: string | null | undefined): string {
  return (value ?? '').toLowerCase()
}

export function filterDonorsBySearch(
  donors: CampaignDonorListItem[],
  query: string
): CampaignDonorListItem[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return donors

  return donors.filter((donor) =>
    [donor.name, donor.email, donor.phone]
      .map((value) => normalizeText(value))
      .some((value) => value.includes(normalizedQuery))
  )
}

export function filterSalesBySearch(
  sales: CampaignSaleListItem[],
  query: string
): CampaignSaleListItem[] {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return sales

  return sales.filter((sale) =>
    [
      sale.donor_name,
      sale.email,
      String(sale.payment_id),
    ]
      .map((value) => normalizeText(value))
      .some((value) => value.includes(normalizedQuery))
  )
}

export function sortDonors(
  donors: CampaignDonorListItem[],
  key: DonorSortKey,
  direction: 'asc' | 'desc'
): CampaignDonorListItem[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...donors].sort((left, right) => {
    const leftValue = left[key]
    const rightValue = right[key]

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * factor
    }

    return normalizeText(String(leftValue)).localeCompare(
      normalizeText(String(rightValue))
    ) * factor
  })
}

export function sortSales(
  sales: CampaignSaleListItem[],
  key: SalesSortKey,
  direction: 'asc' | 'desc'
): CampaignSaleListItem[] {
  const factor = direction === 'asc' ? 1 : -1
  return [...sales].sort((left, right) => {
    const leftValue = left[key]
    const rightValue = right[key]

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * factor
    }

    return normalizeText(String(leftValue)).localeCompare(
      normalizeText(String(rightValue))
    ) * factor
  })
}

export function getCampaignDisplayName(campaign: CampaignDashboardCampaign): string {
  return campaign.title?.trim() || campaign.name || 'Campanha'
}

export function getStatusLabel(status: CampaignDashboardCampaign['status']): string {
  switch (status) {
    case 'active':
      return 'Ativa'
    case 'draft':
      return 'Rascunho'
    case 'finished':
      return 'Finalizada'
    case 'cancelled':
      return 'Cancelada'
    default:
      return 'Sem status'
  }
}

export function getDefaultDashboardRange(now?: Date): DashboardDateRange {
  return getPresetRange('30d', now)
}

export function isSameRange(
  left: Pick<DashboardDateRange, 'preset' | 'dateFrom' | 'dateTo'>,
  right: Pick<DashboardDateRange, 'preset' | 'dateFrom' | 'dateTo'>
): boolean {
  return (
    left.preset === right.preset &&
    left.dateFrom === right.dateFrom &&
    left.dateTo === right.dateTo
  )
}

export function formatCompactDate(value: string | null | undefined): string {
  if (!value) return '-'
  const date = parseDisplayDate(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleDateString('pt-BR')
}

export function formatDateTime(value: string | null | undefined): string {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('pt-BR')
}

export function formatCurrency(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return 'R$ 0,00'
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(parsed)) return 'R$ 0,00'
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(parsed)
}

export function formatNumber(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '-'
  const parsed = typeof value === 'string' ? Number.parseFloat(value) : value
  if (Number.isNaN(parsed)) return '-'
  return new Intl.NumberFormat('pt-BR').format(parsed)
}
